export function createFukushizaiGroupCopyService(deps) {
    // 複写対象から context/no などの非コピー項目を除外します。
    function getFukushizaiGroupCopyFieldIds() {
        const excluded = new Set([
            ...deps.getFukushizaiContextFieldIds(),
            "SSDZ_NO",
            "SSDZ_SONOTAFUZOKUCD6"
        ].map((fieldId) => String(fieldId ?? "").trim().toUpperCase()));

        return deps.getFukushizaiBaseFieldIds()
            .filter((fieldId) => {
                const cid = String(fieldId ?? "").trim().toUpperCase();
                return cid !== "" && !excluded.has(cid);
            });
    }

    // preview 用の UI 補助項目を除去して通常 detail 行へ戻します。
    function stripFukushizaiCopyPreviewFields(row) {
        if (deps.isEmpty(row) || typeof row !== "object") return row;
        Object.keys(row).forEach((key) => {
            if (String(key ?? "").startsWith("__fukushizaiCopyPreview")) delete row[key];
        });
        delete row.__fukushizaiCopyPreview;
        return deps.clearFukushizaiDisplayProjectionFields(row);
    }

    // preview 行を detail 113 へ反映します。
    function applyFukushizaiCopyPreviewRowsToDetail(preview) {
        const previewRows = Array.isArray(deps.DicVal(preview, "previewRows", []))
            ? deps.DicVal(preview, "previewRows", [])
            : [];
        if (previewRows.length === 0) return false;

        const targetItem = deps.normalizeItemCd(deps.DicVal(preview, "targetItem", ""));
        const targetGroup = deps.normalizeGroupNo(deps.DicVal(preview, "targetGroup", ""));
        if (targetItem === "" || targetGroup === "") return false;

        const detailRows = deps.getDetailRowsByPanelType(deps.entrydetailRef.current, deps.FUKUSHIZAI_BASE_PANEL_TYPE);
        if (!Array.isArray(detailRows)) return false;

        const keptRows = detailRows.filter((row) => {
            if (deps.isEmpty(row)) return false;
            return !deps.sameItemGroup(row, targetItem, targetGroup);
        });

        const nextRows = [...keptRows];
        previewRows.forEach((row) => {
            if (deps.isEmpty(row)) return;
            const nextRow = stripFukushizaiCopyPreviewFields({ ...row });
            nextRow[deps.ROW_ID_FIELD] = String(nextRows.length);
            nextRow[deps.FUKUSHIZAI_FIELD.ITEM_CD] = targetItem;
            nextRow[deps.FUKUSHIZAI_FIELD.GROUP_NO] = targetGroup;
            nextRows.push(deps.clearFukushizaiDisplayProjectionFields(nextRow));
        });

        const hitKey = Object.keys(deps.entrydetailRef.current).find((key) => deps.normalizeTypeKey(key) === deps.FUKUSHIZAI_BASE_PANEL_TYPE)
            || deps.FUKUSHIZAI_BASE_PANEL_TYPE;
        deps.entrydetailRef.current[hitKey] = nextRows;
        return true;
    }

    // グループ複写を即時反映し、後段の UI/投影同期は既存 service へ委譲します。
    function applyFukushizaiGroupCopyNow(targetItem, targetGroup, sourceItem, sourceGroup) {
        const resolvedSourceItem = deps.normalizeItemCd(sourceItem);
        const resolvedSourceGroup = deps.normalizeGroupNo(sourceGroup);
        const copyTarget = deps.resolveFukushizaiGroupCopyTargetContext(
            targetItem,
            targetGroup,
            resolvedSourceItem,
            resolvedSourceGroup
        );
        const resolvedTargetItem = copyTarget.itemCd;
        const resolvedTargetGroup = copyTarget.groupNo;

        if (resolvedTargetItem === ""
            || resolvedTargetGroup === ""
            || resolvedSourceItem === ""
            || resolvedSourceGroup === "") {
            return null;
        }

        const changed = deps.applyFukushizaiGroupCopyToState(
            { itemCd: resolvedTargetItem, groupNo: resolvedTargetGroup },
            { itemCd: resolvedSourceItem, groupNo: resolvedSourceGroup }
        );
        if (!changed) return null;

        deps.setPendingFukushizaiCopy(null);
        deps.refreshFukushizaiCopyPreviewGridEditLock(false);
        deps.applyFukushizaiMutationRefresh({
            itemCd: resolvedTargetItem,
            groupNo: resolvedTargetGroup,
            clearMergedFieldsFirst: false,
            markModified: true,
            previewOverride: null
        });

        deps.debugFukushizaiSyncLog("[Kakousiji][fukushizai group copy applied]", {
            sourceContext: { itemCd: resolvedSourceItem, groupNo: resolvedSourceGroup },
            targetContext: { itemCd: resolvedTargetItem, groupNo: resolvedTargetGroup }
        });

        return {
            changed: true,
            preview: false,
            sourceContext: { itemCd: resolvedSourceItem, groupNo: resolvedSourceGroup },
            targetContext: { itemCd: resolvedTargetItem, groupNo: resolvedTargetGroup }
        };
    }

    // preview 確定後は detail 反映と UI 再同期だけを実行します。
    function commitFukushizaiCopyPreview() {
        const preview = typeof deps.getPendingFukushizaiCopy === "function"
            ? deps.getPendingFukushizaiCopy()
            : null;

        if (deps.isEmpty(preview)) {
            deps.setPendingFukushizaiCopy(null);
            deps.refreshFukushizaiCopyPreviewGridEditLock(false);
            return;
        }

        const targetItem = deps.normalizeItemCd(deps.DicVal(preview, "targetItem", ""));
        const targetGroup = deps.normalizeGroupNo(deps.DicVal(preview, "targetGroup", ""));
        const changed = applyFukushizaiCopyPreviewRowsToDetail(preview);
        deps.updateSagefudaItemFlags([targetItem]);

        deps.debugFukushizaiSyncLog("[Kakousiji][fukushizai copy commit]", {
            targetItem,
            targetGroup,
            sourceItem: deps.DicVal(preview, "sourceItem", ""),
            sourceGroup: deps.DicVal(preview, "sourceGroup", ""),
            previewRowsLength: Array.isArray(deps.DicVal(preview, "previewRows", []))
                ? deps.DicVal(preview, "previewRows", []).length
                : 0
        });

        deps.applyFukushizaiMutationRefresh({
            itemCd: targetItem,
            groupNo: targetGroup,
            clearMergedFieldsFirst: false,
            clearPendingPreview: true,
            unlockPreviewGrid: true,
            markModified: changed,
            previewOverride: null
        });
    }

    // preview 取消は既存の UI 復元 service へ委譲します。
    function cancelFukushizaiCopyPreview() {
        deps.cancelFukushizaiCopyPreviewUi();
    }

    return {
        getFukushizaiGroupCopyFieldIds,
        applyFukushizaiGroupCopyNow,
        commitFukushizaiCopyPreview,
        cancelFukushizaiCopyPreview
    };
}
